<template>
  <div>
    <div v-if="forPeoplePage" class="m-bx2">
      <donut-input-text v-model="userSearchQuery" class="w-x54" placeholder="Search users..." />
    </div>
    <template v-else>
      <div class="f-je m-bx2">
        <donut-selector-prefabs-date-range :update-range="updateDateRange" :include-weekends="true" />
        <button
          v-if="state.currentUser.paid_points_admin"
          v-tooltip="'Send report to Slack as a CSV'"
          :class="{ 'm-lx2 inv': true, loading: state.pointsLeaderboard.exportLoading }"
          @click="exportUsersData"
        >
          <i class="mi">file_download</i>
          <div class="t p-l">Export</div>
        </button>
      </div>
    </template>
    <donut-table :headers="tableHeaders" :data="tableData">
      <template #user="{ entry }">
        <div class="f f-gap-4">
          <i
            v-if="!walletIsActive(entry.id)"
            v-tooltip="{content: deactivatedWalletTooltip}"
            class="mi md-18 gray m-l-22"
          >info</i>
          <onboarding-membership-avatar :avatar="entry.avatar" class="w-min-x3" />
          <a v-if="state.currentUser.paid_shoutouts_admin" :href="`/shoutouts/profile?user_id=${entry.id}`" class="link m-l" @click="trackUserClick(entry.id)">{{ entry.name }}</a>
          <div v-else class="t m-l">{{ entry.name }}</div>
        </div>
      </template>
      <template #given="{ entry }">
        <div v-emoji="`${entry.points.given.toLocaleString()} :${props.config.emoji}:`" class="t" />
      </template>
      <template #received="{ entry }">
        <div v-emoji="`${entry.points.received.toLocaleString()} :${props.config.emoji}:`" class="t" />
      </template>
      <template #redeemed="{ entry }">
        <div v-emoji="`${entry.points.redeemed.toLocaleString()} :${props.config.emoji}:`" class="t" />
      </template>
      <template #intros="{ entry }">
        <div v-emoji="`${entry.points.intros.toLocaleString()} :${props.config.emoji}:`" class="t" />
      </template>
      <template #allowance="{ entry, rowIdx }">
        <shoutouts-leaderboard-allowance-input
          :ref="(el) => allowanceInputRefs[`allowance-input${rowIdx}`] = el"
          :user="entry"
          :paywall="allowancePaywall"
          @advance="focusNextAllowanceInput(rowIdx + 1)"
        />
      </template>
      <template #state="{ entry }"><shoutouts-leaderboard-state-toggle :user="entry" /></template>
      <template #role="{ entry }">
        <shoutouts-leaderboard-role-input :user="entry" />
      </template>
    </donut-table>
    <div v-if="isFetching" class="f-jc m-bx2"><donut-loading-dots /></div>
    <div v-else-if="!tableData.length" class="f-jc m-vx2">
      <div class="t">Nobody has received any {{ props.config.points_name_plural }} yet!</div>
      <div v-if="!dateRangeIsAllTime" class="t">&nbsp;Try adjusting the date window to see more.</div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, inject, onBeforeMount } from 'vue';
  import { useStore } from 'vuex';
  import useInfiniteScroll from 'composables/infinite-scroll';

  const { state, getters, commit, dispatch } = useStore();
  const $i18n = inject('$i18n');

  const props = defineProps({
    config: { type: Object, required: true },
    program: { type: Object, default: null },
    headers: { type: Array, required: true },
    forPeoplePage: { type: Boolean, default: false },
  });

  const walletIsActive = (userId) => state.pointsLeaderboard.asyncValuesByUser[userId].state !== 'inactive';

  // Paywalling
  const allowancePaywall = {
    data: props.program?.upgrade_data,
    feature: 'member_allowance',
    featureTitleText: 'set custom allowances',
    source: 'shoutouts-people-custom-allowance',
  };

  // Current User Collection
  const collection = computed(() => getters['pointsLeaderboard/currentUserCollection']);
  const tableData = computed(() => {
    const list = collection.value?.list || [];
    return list.map((entry) => {
      if (!walletIsActive(entry.id)) {
        return {
          ...entry,
          classes: 'o-35',
        };
      }

      return entry;
    });
  });
  const deactivatedWalletTooltip = 'This user\'s points are disabled. They can not send or receive '
    + `${$i18n.t('shoutouts.point', { count: 2 })} or redeem from the ${$i18n.t('shoutouts.rewards_store')}.`;

  // Data Fetching
  const nextPage = computed(() => (collection.value ? collection.value.nextPage : 1));
  const isFetching = computed(() => state.pointsLeaderboard.isFetching);
  const fetchData = () => dispatch('pointsLeaderboard/fetchUsers', { page: nextPage.value });
  const shouldFetchAtEndOfScroll = computed(() => nextPage.value && !isFetching.value);
  const { fetchFirstPage } = useInfiniteScroll({ fetchData, shouldFetchAtEndOfScroll });

  // Export data
  const exportUsersData = () => dispatch('pointsLeaderboard/exportUsersData');

  const tableHeaders = computed(() => {
    const headerList = props.headers.map((header) => {
      const sortingInfo = header.sortable ? { orderCol: header.key, action: 'pointsLeaderboard/toggleSorting', fetchFirstPage } : null;
      const icons = header.tooltip ? { icons: [{ name: 'info', tooltip: header.tooltip }] } : null;
      return { ...header, ...sortingInfo, ...icons };
    });
    const activeHeader = headerList.find(header => header?.orderCol === state.pointsLeaderboard.orderCol);
    if (!activeHeader.icons) activeHeader.icons = [];
    activeHeader.icons.push({ name: state.pointsLeaderboard.orderDir === 'desc' ? 'keyboard_arrow_down' : 'keyboard_arrow_up' });
    return headerList;
  });

  const initializeState = () => {
    const initOrderCol = props.headers.find(h => h.sortable).key; // First sortable header
    commit('update', { module: 'pointsLeaderboard', key: 'orderCol', value: initOrderCol });
    commit('update', { module: 'pointsLeaderboard', key: 'orderDir', value: props.forPeoplePage ? 'asc' : 'desc' });
    commit('update', { module: 'pointsLeaderboard', key: 'forPeoplePage', value: props.forPeoplePage });
  };

  // User Links
  const trackUserClick = (userId) => {
    window.donut.track('Shoutouts Profile — Navigated to Other User', { userId, source: 'leaderboard' });
  };

  // Allowance (People Page Only)
  const allowanceInputRefs = ref({});
  const focusNextAllowanceInput = (idx) => {
    const allowanceInputComponent = allowanceInputRefs.value[`allowance-input${idx}`];
    if (allowanceInputComponent) {
      allowanceInputComponent.open();
    }
  };

  // Date Window Selector
  const updateDateRange = ({ startDate, endDate }) => {
    dispatch('pointsLeaderboard/updateDateRange', { startDate, endDate, fetchFirstPage });
  };
  const dateRangeIsAllTime = computed(() => !(state.pointsLeaderboard.startDate || state.pointsLeaderboard.endDate));

  // User Search (People Page Only)
  const userSearchTimeout = ref(null);
  const userSearchQuery = computed({
    get() { return state.pointsLeaderboard.userSearchQuery; },
    set(value) {
      if (userSearchTimeout.value) clearTimeout(userSearchTimeout.value);
      userSearchTimeout.value = setTimeout(() => {
        dispatch('pointsLeaderboard/updateUserSearchQuery', { userSearchQuery: value?.trim(), fetchFirstPage });
      }, 350);
    },
  });

  // Lifecycle Hooks
  onBeforeMount(() => {
    initializeState();
  });
</script>
